import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import isEqual from "lodash/isEqual";

const createTaskFields = (title = "") => ({
  title,
  isFlatRate: true,
  flatRate: "",
  includedHours: "",
  hourlyRate: "",
  chargeOverageHours: false,
});

const createRepRateFields = () => ({
  weekdayRateTitle: "Rep Hours Weekdays",
  weekdayNumberOfFTEs: "",
  weekdayHoursPerDay: "",
  weekdayIncludeInInvoice: true,
  weekdayRate: "",
  weekendRateTitle: "Rep Hours Weekends",
  weekendNumberOfFTEs: "",
  weekendHoursPerDay: "",
  weekendIncludeInInvoice: true,
  weekendRate: "",
  socialMediaWeekdayRateTitle: "Social Media Weekdays",
  socialMediaWeekdayNumberOfFTEs: "",
  socialMediaWeekdayHoursPerDay: "",
  socialMediaWeekdayIncludeInInvoice: true,
  socialMediaWeekdayRate: "",
  socialMediaWeekendRateTitle: "Social Media Weekends",
  socialMediaWeekendNumberOfFTEs: "",
  socialMediaWeekendHoursPerDay: "",
  socialMediaWeekendIncludeInInvoice: true,
  socialMediaWeekendRate: "",
  overtimeHourRateTitle: "Overtime Charge",
  overtimeIncludeInInvoice: true,
  overtimeHourRate: "",
  holidayHourRateTitle: "Holiday Charge",
  holidayIncludeInInvoice: true,
  holidayHourRate: "",
});

const CurrencyInput = ({
  value,
  onChange,
  isInvalid,
  className = "input-field",
  disabled = false,
}) => (
  <NumericFormat
    value={value ?? ""}
    onValueChange={(values) => onChange?.({ target: { value: values.value } })}
    thousandSeparator
    decimalScale={2}
    allowNegative={false}
    prefix="$ "
    placeholder="$ 0.00"
    customInput={Form.Control}
    isInvalid={isInvalid}
    className={className}
    disabled={disabled}
  />
);

const toSectionLabel = (sectionId) => {
  const raw = String(sectionId || "").trim();
  const withoutTaskPrefix = raw.replace(/^task_/, "");
  return withoutTaskPrefix
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveSectionOptionLabel = (sectionId, initialSectionData = {}) => {
  return (
    initialSectionData?.section_labels?.[sectionId] ||
    initialSectionData?.sections?.[sectionId]?.title ||
    initialSectionData?.[sectionId]?.title ||
    toSectionLabel(sectionId)
  );
};

const getVisibleSectionOptions = (
  selectedSections = [],
  sectionOptions = [],
  initialSectionData = {}
) => {
  if (Array.isArray(sectionOptions) && sectionOptions.length > 0) {
    const selectedIds = Array.isArray(selectedSections) ? selectedSections : [];

    const baseOptions = selectedIds.length
      ? sectionOptions.filter((option) => selectedIds.includes(option.id))
      : sectionOptions;

    const existingIds = new Set(baseOptions.map((option) => option.id));
    const missingSelectedOptions = selectedIds
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        id,
        label: resolveSectionOptionLabel(id, initialSectionData),
      }));

    if (missingSelectedOptions.length > 0) {
      return [...baseOptions, ...missingSelectedOptions];
    }

    return baseOptions;
  }

  return (selectedSections || []).map((sectionId) => ({
    id: sectionId,
    label: resolveSectionOptionLabel(sectionId, initialSectionData),
  }));
};

const LaborTab = ({
  selectedSections = [],
  sectionOptions = [],
  onSectionDataChange,
  initialSectionData = {},
  getFieldError = () => "",
}) => {
  const visibleSectionOptions = useMemo(
    () => getVisibleSectionOptions(selectedSections, sectionOptions, initialSectionData),
    [initialSectionData, sectionOptions, selectedSections]
  );

  const [sectionValues, setSectionValues] = useState({});

  const oldInitialRef = useRef(null);
  const oldVisibleOptionsRef = useRef(null);
  const isHydratingRef = useRef(false);
  const lastEmittedPayloadRef = useRef(null);

  useEffect(() => {
    const initialDataUnchanged = isEqual(oldInitialRef.current, initialSectionData);
    const visibleOptionsUnchanged = isEqual(
      oldVisibleOptionsRef.current,
      visibleSectionOptions
    );

    if (initialDataUnchanged && visibleOptionsUnchanged) {
      return;
    }

    isHydratingRef.current = true;

    setSectionValues((prev) => {
      const next = {};

      visibleSectionOptions.forEach((section) => {
        const existing = initialSectionData?.[section.id] || {};
        const prevValue = prev?.[section.id];

        if (section.id === "rep_hours_onshore" || section.id === "rep_hours_offshore") {
          const base = createRepRateFields();
          const source = prevValue || existing;

          next[section.id] = prevValue
            ? {
                ...base,
                ...source,
              }
            : {
                ...base,
                ...source,
              };
          return;
        }

        const base = createTaskFields(section.label);
        const source = prevValue || existing;
        const hasExplicitTitle = Object.prototype.hasOwnProperty.call(source || {}, "title");

        next[section.id] = {
          ...base,
          ...source,
          title: hasExplicitTitle ? source?.title : section.label || "",
          isFlatRate:
            source?.isFlatRate === undefined ? true : Boolean(source?.isFlatRate),
          chargeOverageHours: Boolean(source?.chargeOverageHours),
        };
      });

      if (isEqual(next, prev)) {
        return prev;
      }

      return next;
    });

    oldInitialRef.current = initialSectionData;
    oldVisibleOptionsRef.current = visibleSectionOptions;
  }, [initialSectionData, visibleSectionOptions]);

  const onSectionDataChangeRef = useRef(onSectionDataChange);
  useEffect(() => {
    onSectionDataChangeRef.current = onSectionDataChange;
  }, [onSectionDataChange]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }

    if (isEqual(lastEmittedPayloadRef.current, sectionValues)) {
      return;
    }

    if (isEqual(sectionValues, oldInitialRef.current)) {
      return;
    }

    lastEmittedPayloadRef.current = sectionValues;
    onSectionDataChangeRef.current?.(sectionValues);
  }, [sectionValues]);

  const hasSelectedSection = visibleSectionOptions.length > 0;

  const handleFieldChange = (sectionId, field, value) => {
    setSectionValues((prev) => ({
      ...prev,
      [sectionId]: {
        ...(prev?.[sectionId] || createTaskFields()),
        [field]: value,
      },
    }));
  };

  const getError = (sectionId, field) => getFieldError?.(sectionId, field) || "";

  const renderRepRateSection = (sectionId, title, values) => {
    const includeValue = (fieldName) =>
      values[fieldName] === undefined ? true : Boolean(values[fieldName]);

    const repRows = [
      {
        key: "weekday",
        titleField: "weekdayRateTitle",
        includeField: "weekdayIncludeInInvoice",
        ftesField: "weekdayNumberOfFTEs",
        hoursField: "weekdayHoursPerDay",
        rateField: "weekdayRate",
      },
      {
        key: "weekend",
        titleField: "weekendRateTitle",
        includeField: "weekendIncludeInInvoice",
        ftesField: "weekendNumberOfFTEs",
        hoursField: "weekendHoursPerDay",
        rateField: "weekendRate",
      },
      {
        key: "socialMediaWeekday",
        titleField: "socialMediaWeekdayRateTitle",
        includeField: "socialMediaWeekdayIncludeInInvoice",
        ftesField: "socialMediaWeekdayNumberOfFTEs",
        hoursField: "socialMediaWeekdayHoursPerDay",
        rateField: "socialMediaWeekdayRate",
      },
      {
        key: "socialMediaWeekend",
        titleField: "socialMediaWeekendRateTitle",
        includeField: "socialMediaWeekendIncludeInInvoice",
        ftesField: "socialMediaWeekendNumberOfFTEs",
        hoursField: "socialMediaWeekendHoursPerDay",
        rateField: "socialMediaWeekendRate",
      },
    ];

    const additionalChargeRows = [
      {
        key: "overtime",
        titleField: "overtimeHourRateTitle",
        includeField: "overtimeIncludeInInvoice",
        rateField: "overtimeHourRate",
      },
      {
        key: "holiday",
        titleField: "holidayHourRateTitle",
        includeField: "holidayIncludeInInvoice",
        rateField: "holidayHourRate",
      },
    ];

    return (
      <>
        <div className="labor-hours-table-wrapper mb-3">
          <div className="labor-hours-table-inner">
            <div className="labor-hours-grid labor-hours-header-row">
              <div className="labor-hours-header-cell">Work Category</div>
              <div className="labor-hours-header-cell">Add to Invoice</div>
              <div className="labor-hours-header-cell"># of FTEs</div>
              <div className="labor-hours-header-cell"># Hours/Day</div>
              <div className="labor-hours-header-cell">Hourly Rate ($)</div>
            </div>

            {repRows.map((row) => {
              const includeInInvoice = includeValue(row.includeField);

              return (
                <div className="labor-hours-grid labor-hours-data-row" key={row.key}>
                  <div className="labor-hours-category-cell">
                    <Form.Control
                      type="text"
                      value={values[row.titleField] ?? ""}
                      onChange={(event) =>
                        handleFieldChange(sectionId, row.titleField, event.target.value)
                      }
                      isInvalid={Boolean(getError(sectionId, row.titleField))}
                      disabled={!includeInInvoice}
                      className="input-field"
                    />
                  </div>

                  <div className="d-flex justify-content-center">
                    <Form.Check
                      type="checkbox"
                      checked={includeInInvoice}
                      onChange={(event) =>
                        handleFieldChange(sectionId, row.includeField, event.target.checked)
                      }
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Form.Control
                      type="number"
                      min="0"
                      value={values[row.ftesField]}
                      onChange={(event) =>
                        handleFieldChange(sectionId, row.ftesField, event.target.value)
                      }
                      isInvalid={Boolean(getError(sectionId, row.ftesField))}
                      disabled={!includeInInvoice}
                      className="labor-hours-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError(sectionId, row.ftesField)}
                    </Form.Control.Feedback>
                  </div>

                  <div>
                    <Form.Control
                      type="number"
                      min="0"
                      value={values[row.hoursField]}
                      onChange={(event) =>
                        handleFieldChange(sectionId, row.hoursField, event.target.value)
                      }
                      isInvalid={Boolean(getError(sectionId, row.hoursField))}
                      disabled={!includeInInvoice}
                      className="labor-hours-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError(sectionId, row.hoursField)}
                    </Form.Control.Feedback>
                  </div>

                  <div>
                    <CurrencyInput
                      value={values[row.rateField]}
                      onChange={(event) =>
                        handleFieldChange(sectionId, row.rateField, event.target.value)
                      }
                      isInvalid={Boolean(getError(sectionId, row.rateField))}
                      disabled={!includeInInvoice}
                      className="labor-hours-input"
                    />
                    <Form.Control.Feedback type="invalid">
                      {getError(sectionId, row.rateField)}
                    </Form.Control.Feedback>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <h4 className="font-14 font-weight-bold heading-color mb-2">Additional Charges</h4>
        {additionalChargeRows.map((row) => {
          const includeInInvoice = includeValue(row.includeField);

          return (
            <Row key={row.key}>
              <Col lg={7}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={values[row.titleField]}
                    onChange={(event) =>
                      handleFieldChange(sectionId, row.titleField, event.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.titleField))}
                    disabled={!includeInInvoice}
                    className="input-field"
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.titleField)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Hourly Rate ($)</Form.Label>
                  <CurrencyInput
                    value={values[row.rateField]}
                    onChange={(event) =>
                      handleFieldChange(sectionId, row.rateField, event.target.value)
                    }
                    isInvalid={Boolean(getError(sectionId, row.rateField))}
                    disabled={!includeInInvoice}
                  />
                  <Form.Control.Feedback type="invalid">
                    {getError(sectionId, row.rateField)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col lg={2}>
                <Form.Group className="mb-3">
                  <Form.Label className="label-input">Add to Invoice</Form.Label>
                  <Form.Check
                    type="checkbox"
                    checked={includeInInvoice}
                    onChange={(event) =>
                      handleFieldChange(sectionId, row.includeField, event.target.checked)
                    }
                    className="mt-2"
                  />
                </Form.Group>
              </Col>
            </Row>
          );
        })}
      </>
    );
  };

  return (
    <div className="labor-tab-section">
      {!hasSelectedSection && (
        <div className="invoice-section-div mb-0 text-center">
          <span className="font-16 font-weight-bold heading-color">
            No labor tasks found for this program.
          </span>
        </div>
      )}

      {visibleSectionOptions.map((section, index) => {
        const isRepSection =
          section.id === "rep_hours_onshore" || section.id === "rep_hours_offshore";
        const values = isRepSection
          ? sectionValues?.[section.id] || createRepRateFields()
          : sectionValues?.[section.id] || createTaskFields(section.label);

        return (
          <div
            className={`invoice-section-div ${index === visibleSectionOptions.length - 1 ? "mb-0" : ""}`}
            key={section.id}
          >
            <h3 className="d-flex align-items-center gap-2">
              <img src="/assets/images/invoice-user-icon.svg" alt="" />
              <span className="font-16 font-weight-bold heading-color">
                {section.label}
              </span>
            </h3>

            {isRepSection ? (
              renderRepRateSection(section.id, section.label, values)
            ) : (
              <>
                <Row>
                  <Col lg={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Charge flat rate (uncheck for hourly rate)"
                        checked={Boolean(values.isFlatRate)}
                        onChange={(event) =>
                          handleFieldChange(section.id, "isFlatRate", event.target.checked)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Title</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.title}
                        onChange={(event) =>
                          handleFieldChange(section.id, "title", event.target.value)
                        }
                        isInvalid={Boolean(getError(section.id, "title"))}
                        className="input-field"
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError(section.id, "title")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  {Boolean(values.isFlatRate) ? (
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label className="label-input">Flat Rate ($)</Form.Label>
                        <CurrencyInput
                          value={values.flatRate}
                          onChange={(event) =>
                            handleFieldChange(section.id, "flatRate", event.target.value)
                          }
                          isInvalid={Boolean(getError(section.id, "flatRate"))}
                        />
                        <Form.Control.Feedback type="invalid">
                          {getError(section.id, "flatRate")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  ) : null}
                </Row>

                <Row>
                  {Boolean(values.isFlatRate) ? (
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label className="label-input">Included Hours</Form.Label>
                        <Form.Control
                          type="text"
                          value={values.includedHours}
                          onChange={(event) =>
                            handleFieldChange(section.id, "includedHours", event.target.value)
                          }
                          isInvalid={Boolean(getError(section.id, "includedHours"))}
                          className="input-field"
                        />
                        <Form.Control.Feedback type="invalid">
                          {getError(section.id, "includedHours")}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  ) : null}
                  <Col>
                    <Form.Group className="mb-3">
                      <Form.Label className="label-input">Hourly Rate</Form.Label>
                      <CurrencyInput
                        value={values.hourlyRate}
                        onChange={(event) =>
                          handleFieldChange(section.id, "hourlyRate", event.target.value)
                        }
                        isInvalid={Boolean(getError(section.id, "hourlyRate"))}
                      />
                      <Form.Control.Feedback type="invalid">
                        {getError(section.id, "hourlyRate")}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {Boolean(values.isFlatRate) ? (
                  <Form.Group className="mb-0">
                    <Form.Check
                      type="checkbox"
                      label="Charge overage hours"
                      checked={Boolean(values.chargeOverageHours)}
                      onChange={(event) =>
                        handleFieldChange(
                          section.id,
                          "chargeOverageHours",
                          event.target.checked
                        )
                      }
                    />
                  </Form.Group>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LaborTab;